/**
 * Floor contact markers and detrended height series for analytics charts.
 *
 * Availability: floor_detect present (GET /a/get-floor-detect, or trial.floor_detect
 *   on GET /a/get-trial-by-id). Any floor object counts (stance_heel_ransac,
 *   charuco_board, or otherwise). Toggles show when floor metadata exists — even
 *   if contact event columns are empty. Hide that chrome when floor JSON is absent.
 * Height: {heel|toe}_{left|right}_floor_h_dt as a line. heading_sign is not a
 *   metric and is never a scale factor on height.
 * Events: sparse per-frame columns via GET /a/get-values-metrics?type=angles&name=…
 *   e.g. heel_left_contact, toe_right_off — finite sample at event frame, null
 *   elsewhere, drawn as scatter (no line through the nulls).
 * Time: t_i = i / hz, where hz prefers floor_detect.fps (~59.94), else
 *   values.length / duration_sec, else 60.
 */

export const CONTACT_EVENT_TYPES = [
  'heel_contact',
  'heel_off',
  'toe_contact',
  'toe_off',
];

export const CONTACT_TYPE_LABELS = {
  heel_contact: 'Heel contact',
  heel_off: 'Heel off',
  toe_contact: 'Toe contact',
  toe_off: 'Toe off',
};

export const DEFAULT_CONTACT_TYPE_VISIBLE = {
  heel_contact: true,
  heel_off: true,
  toe_contact: true,
  toe_off: true,
};

/** Angle-table column names that carry contact / off event samples. */
export const CONTACT_ANGLE_COLUMNS = [
  'heel_left_contact',
  'heel_right_contact',
  'toe_left_contact',
  'toe_right_contact',
  'heel_left_off',
  'heel_right_off',
  'toe_left_off',
  'toe_right_off',
];

export const HEEL_MARKER_COLOR = '#1f77b4';
export const TOE_MARKER_COLOR = '#ff7f0e';

/** Default height lines. Raw `*_floor_h` stays in the catalog; these are plotted. */
export const FLOOR_HEIGHT_DT_COLUMNS = [
  'heel_left_floor_h_dt',
  'heel_right_floor_h_dt',
  'toe_left_floor_h_dt',
  'toe_right_floor_h_dt',
];

const DEFAULT_CONTACT_HZ = 60;

/**
 * Map landmark + event to a visibility key used by Redux toggles.
 */
export function contactTypeKey(landmark, event) {
  const lm = String(landmark || '').toLowerCase();
  const ev = String(event || '').toLowerCase();
  const isHeel = lm === 'heel' || lm.startsWith('heel_');
  const isToe =
    lm === 'big_toe' ||
    lm === 'toe' ||
    lm.startsWith('big_toe_') ||
    lm.startsWith('toe_');
  if (isHeel) {
    if (ev === 'contact') return 'heel_contact';
    if (ev === 'off') return 'heel_off';
    return null;
  }
  if (isToe) {
    if (ev === 'contact') return 'toe_contact';
    if (ev === 'off') return 'toe_off';
    return null;
  }
  return null;
}

/**
 * Parse an angles column name like heel_left_contact / toe_right_off.
 * @returns {{ landmark: 'heel'|'toe', side: 'left'|'right', event: 'contact'|'off', typeKey: string }|null}
 */
export function parseContactAngleColumn(name) {
  const key = String(name || '').toLowerCase();
  const match = key.match(/^(heel|toe|big_toe)_(left|right)_(contact|off)$/);
  if (!match) return null;
  const landmark = match[1] === 'big_toe' ? 'toe' : match[1];
  const side = match[2];
  const event = match[3];
  const typeKey = `${landmark === 'heel' ? 'heel' : 'toe'}_${event}`;
  return { landmark, side, event, typeKey };
}

/**
 * True when floor metadata is present. `source` / `floor_kind` are not required,
 * and `charuco_floor` alone does not count.
 */
export function hasFloorDetect(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.status === 'failure') return false;
  const floor = payload.floor_detect;
  return !!floor && typeof floor === 'object' && !Array.isArray(floor);
}

/** heading_sign is stored on the trial clock and must not be drawn or used as a gain. */
export function isHeadingSignMetric(name) {
  return String(name || '').toLowerCase() === 'heading_sign';
}

/** Detrended or raw floor-height angle columns (meters, not degrees). */
export function isFloorHeightMetric(name) {
  return /^(heel|toe)_(left|right)_floor_h(_dt)?$/.test(String(name || '').toLowerCase());
}

/**
 * Angle names that may be line charts. Contact/off columns are scatter events.
 * heading_sign is omitted so it cannot scale or replace a height series.
 */
export function isPlottableAngleMetric(name) {
  if (name == null || name === '') return false;
  if (isHeadingSignMetric(name)) return false;
  if (parseContactAngleColumn(name)) return false;
  return true;
}

export function filterPlottableAngleMetrics(names) {
  if (!Array.isArray(names)) return [];
  return names.filter(isPlottableAngleMetric);
}

/** Copy a metrics dict with the angles catalog limited to line-chart names. */
export function angleCatalogForCharts(dict) {
  if (!dict || typeof dict !== 'object') return dict;
  if (!Array.isArray(dict.angles)) return dict;
  return { ...dict, angles: filterPlottableAngleMetrics(dict.angles) };
}

/**
 * Resolve sample rate for contact and floor-height timing.
 * floor_detect.fps is the trial clock (~59.94) when a replay needs it.
 */
export function resolveContactSampleRateHz(metricSeries, floorDetect) {
  const fps = floorDetect?.fps;
  if (typeof fps === 'number' && Number.isFinite(fps) && fps > 0) return fps;

  const n = metricSeries?.values?.length;
  const dur = metricSeries?.duration_sec;
  if (
    typeof n === 'number' &&
    n > 0 &&
    typeof dur === 'number' &&
    Number.isFinite(dur) &&
    dur > 0
  ) {
    return n / dur;
  }
  return DEFAULT_CONTACT_HZ;
}

/**
 * Extract contact events from one sparse angle series response.
 */
export function eventsFromSparseAngleSeries(columnName, metricSeries, hz) {
  const meta = parseContactAngleColumn(columnName);
  if (!meta) return [];
  const values = metricSeries?.values;
  if (!Array.isArray(values) || values.length === 0) return [];
  const rate =
    typeof hz === 'number' && Number.isFinite(hz) && hz > 0 ? hz : DEFAULT_CONTACT_HZ;

  const out = [];
  for (let i = 0; i < values.length; i += 1) {
    const row = values[i];
    const y = Array.isArray(row) ? row[0] : row;
    if (typeof y !== 'number' || !Number.isFinite(y)) continue;
    out.push({
      t: i / rate,
      side: meta.side,
      landmark: meta.landmark,
      event: meta.event,
      typeKey: meta.typeKey,
      height: y,
      frame: i,
      column: columnName,
    });
  }
  return out;
}

/**
 * Merge events from multiple contact angle series into a sorted list.
 * @param {{ name: string, series: object|null|undefined }[]} seriesList
 * @param {object|null} floorDetect - floor_detect object (for fps)
 */
export function buildContactEventsFromAngleSeries(seriesList, floorDetect) {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return [];
  const out = [];
  for (let i = 0; i < seriesList.length; i += 1) {
    const entry = seriesList[i];
    if (!entry?.name) continue;
    const hz = resolveContactSampleRateHz(entry.series, floorDetect);
    const events = eventsFromSparseAngleSeries(entry.name, entry.series, hz);
    for (let j = 0; j < events.length; j += 1) out.push(events[j]);
  }
  out.sort(
    (a, b) => a.t - b.t || String(a.typeKey).localeCompare(String(b.typeKey))
  );
  return out;
}

export function hasFloorContacts(events) {
  return Array.isArray(events) && events.length > 0;
}

/**
 * Linear interpolate y at time t on sorted line points [{x,y}, ...].
 */
export function interpolateYAtTime(linePoints, t) {
  if (!Array.isArray(linePoints) || linePoints.length === 0) return null;
  if (typeof t !== 'number' || !Number.isFinite(t)) return null;

  let lo = 0;
  let hi = linePoints.length - 1;
  if (t <= linePoints[lo].x) {
    const y = linePoints[lo].y;
    return typeof y === 'number' && Number.isFinite(y) ? y : null;
  }
  if (t >= linePoints[hi].x) {
    const y = linePoints[hi].y;
    return typeof y === 'number' && Number.isFinite(y) ? y : null;
  }

  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (linePoints[mid].x <= t) lo = mid;
    else hi = mid;
  }

  const a = linePoints[lo];
  const b = linePoints[hi];
  const ya = a.y;
  const yb = b.y;
  if (typeof ya !== 'number' || !Number.isFinite(ya)) return null;
  if (typeof yb !== 'number' || !Number.isFinite(yb)) return null;
  return ya + (yb - ya) * ((t - a.x) / (b.x - a.x));
}

/**
 * Infer left/right from a chart metric name (hip_left_flex, isWalk_right, …).
 * Returns null when the metric is not side-specific (e.g. neck_flex).
 */
export function metricSideFromName(metricName) {
  const key = String(metricName || '').toLowerCase();
  if (!key) return null;
  // Prefer explicit _left_ / _right_ segments, then suffix forms (isWalk_left).
  if (/(^|_)(left|ll|lu)(_|$)/.test(key)) return 'left';
  if (/(^|_)(right|rl|ru)(_|$)/.test(key)) return 'right';
  return null;
}

export function filterVisibleContacts(
  events,
  {
    globalVisible = true,
    typeVisible = DEFAULT_CONTACT_TYPE_VISIBLE,
    chartVisible = true,
    side = null,
    sourceVisible = null,
  } = {}
) {
  if (!globalVisible || !chartVisible) return [];
  if (!Array.isArray(events) || events.length === 0) return [];
  const sideFilter =
    side === 'left' || side === 'right' ? side : null;
  const sources = sourceVisible && typeof sourceVisible === 'object' ? sourceVisible : null;
  return events.filter((ev) => {
    const key = ev.typeKey || contactTypeKey(ev.landmark, ev.event);
    if (!key) return false;
    if (typeVisible[key] === false) return false;
    if (sideFilter && ev.side && ev.side !== sideFilter) return false;
    if (sources) {
      const manual = ev.source === 'user';
      if (manual && sources.user === false) return false;
      if (!manual && sources.pipeline === false) return false;
    }
    return true;
  });
}

export function buildContactMarkers(linePoints, events, visibility) {
  const visible = filterVisibleContacts(events, visibility);
  const markers = [];
  for (let i = 0; i < visible.length; i += 1) {
    const ev = visible[i];
    const y = interpolateYAtTime(linePoints, ev.t);
    if (y == null) continue;
    markers.push({
      t: ev.t,
      y,
      landmark: ev.landmark,
      event: ev.event,
      typeKey: ev.typeKey,
      side: ev.side,
      id: ev.id,
      source: ev.source,
    });
  }
  return markers;
}

/**
 * The placed clinician mark under the pointer, if the click lands on it.
 * Markers without an id are pipeline marks and are not removable.
 * When `typeKey` is set, only a mark of that event (the active annotate tool)
 * can be hit, so a heel and a toe stacked on the same sample stay independent.
 * `px`/`py` are plot-overlay pixels. `radius` is the hit size in those pixels.
 */
export function contactMarkerAtPoint(markers, x, y, radius = 12, typeKey = null) {
  if (!Array.isArray(markers)) return null;
  if (typeof x !== 'number' || !Number.isFinite(x)) return null;
  if (typeof y !== 'number' || !Number.isFinite(y)) return null;
  const limit = typeof radius === 'number' && Number.isFinite(radius) && radius > 0 ? radius : 12;
  const typeFilter = typeKey ? String(typeKey) : null;
  let best = null;
  let bestDist = limit;
  for (let i = 0; i < markers.length; i += 1) {
    const marker = markers[i];
    if (!marker || !marker.id) continue;
    if (typeFilter && marker.typeKey !== typeFilter) continue;
    const px = marker.px;
    const py = marker.py;
    if (typeof px !== 'number' || !Number.isFinite(px)) continue;
    if (typeof py !== 'number' || !Number.isFinite(py)) continue;
    const dist = Math.hypot(px - x, py - y);
    if (dist <= bestDist) {
      best = marker;
      bestDist = dist;
    }
  }
  return best;
}

export function markerColorForLandmark(landmark) {
  const lm = String(landmark || '').toLowerCase();
  if (lm === 'heel') return HEEL_MARKER_COLOR;
  return TOE_MARKER_COLOR;
}

export function chartContactKey(metricType, metricName) {
  return `${metricType || 'angles'}:${metricName || ''}`;
}
